import type {
  GameSnapshot,
  PlayerProfile,
  RoomEvent,
  RunResult,
  SessionApi,
  TerritoryPatch,
} from "@games-monorepo/yak-trails-core";

export type YakTrailsScreen = "home" | "practice" | "online" | "team" | "tournaments" | "tournament-admin" | "tutorial" | "party" | "skins" | "connecting" | "playing";

export interface YakTrailsState {
  screen: YakTrailsScreen;
  snapshot: GameSnapshot | null;
  territoryRevision: number;
  result: RunResult | null;
  profile: PlayerProfile | null;
  selectedSkinId: string;
  networkQuality: "good" | "degraded" | "offline";
  message: string | null;
}

export interface YakTrailsProps {
  sessionApi?: SessionApi;
  practiceEnabled?: boolean;
  onlineEnabled?: boolean;
  initialPartySlug?: string;
  initialTournamentId?: string;
  initialAdminScreen?: boolean;
  onBack?: () => void;
  className?: string;
  practiceWorkerUrl?: string;
  territoryWorkerUrl?: string;
  onTelemetry?: (event: string, context: Record<string, unknown>) => void;
}

export interface TerritoryState {
  owners: Array<string | null>;
  revision: number;
}

export function applyTerritoryPatch(state: TerritoryState, patch: TerritoryPatch, gridSize: number): TerritoryState {
  if (!patch.full && patch.baseRevision !== state.revision) return state;
  const owners = patch.full ? new Array<string | null>(gridSize * gridSize).fill(null) : [...state.owners];
  for (const run of patch.changedRuns) {
    const end = Math.min(owners.length, run.start + run.length);
    for (let index = Math.max(0, run.start); index < end; index += 1) owners[index] = run.ownerId;
  }
  return { owners, revision: patch.revision };
}

export function resultFromRoomEvent(event: RoomEvent, selfPlayerId: string): RunResult | null {
  if (event.type === "result") return event.result.playerId === selfPlayerId ? event.result : null;
  if (event.type !== "events" || !selfPlayerId) return null;
  const resultEvent = [...event.events].reverse().find((candidate) => (
    candidate.type === "player_eliminated" || candidate.type === "full_capture"
  ) && candidate.playerId === selfPlayerId);
  if (resultEvent && "result" in resultEvent) return resultEvent.result;
  const teamVictory = [...event.events].reverse().find((candidate) => candidate.type === "team_victory");
  if (teamVictory?.type === "team_victory") {
    return teamVictory.results.find((candidate) => candidate.playerId === selfPlayerId) ?? null;
  }
  return null;
}
